import { useMachine } from "@xstate/react";
import { eventNames, voucheringFlowStateMachine } from "./machine";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { useTranslation } from "react-i18next";
import AddOrUseVoucherPage from "../AddOrUseVoucher";
import DefaultError from "@/features/Common/components/DefaultError";
import { Stack, Typography } from "@mui/material";
import {
  getVoucherPaymentRedirectURL,
  VoucherSubType,
  VoucherType,
} from "@/services/core-api-adapter";
import { DoctorNurseVoucherTypeSelection } from "../DoctorNurseVoucherTypeSelection";
import { BuyVoucherPrompt } from "../BuyVoucherPrompt";
import Breakout from "@/features/Common/components/Breakout";
import { Navigate } from "react-router";
import PaymentFailure from "@/features/Common/components/PaymentFailure";
import VoucherOrderSummary from "../VoucherOrderSummary";
import PaymentReceipt from "@/features/Common/components/PaymentReceipt";

export default function VoucheringFlow() {
  const { t } = useTranslation();
  const [voucheringStateMachine, voucheringStateMachineDispatch] = useMachine(
    voucheringFlowStateMachine
  );

  function onGoHomeButtonClick() {
    voucheringStateMachineDispatch({ type: eventNames.GO_HOME_BUTTON_CLICKED });
  }

  function onDoctorNurseConsultationVoucherClick(voucherType: VoucherType) {
    voucheringStateMachineDispatch({
      type: eventNames.DOCTOR_NURSE_CONSULTATION_VOUCHER_CLICKED,
      input: voucherType,
    });
  }

  function onDoctorVoucherClick(voucherSubType: VoucherSubType) {
    voucheringStateMachineDispatch({
      type: eventNames.DOCTOR_VOUCHER_CLICKED,
      input: voucherSubType,
    });
  }

  function onNurseVoucherClick(voucherSubType: VoucherSubType) {
    voucheringStateMachineDispatch({
      type: eventNames.NURSE_VOUCHER_CLICKED,
      input: voucherSubType,
    });
  }

  function onPathologyTestVoucherClick(voucherType: VoucherType) {
    voucheringStateMachineDispatch({
      type: eventNames.PATHOLOGY_VOUCHER_CLICKED,
      input: voucherType,
    });
  }

  function onBackButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.BACK_BUTTON_CLICKED,
    });
  }

  function onViewPaymentReceiptClick() {
    voucheringStateMachineDispatch({
      type: eventNames.VIEW_RECEIPT_BUTTON_CLICKED,
    });
  }

  function onVoucherPaymentContinueButtonClick() {
    voucheringStateMachineDispatch({
      type: eventNames.CONTINUE_BUTTON_CLICKED,
    });
  }

  function onBreakoutDisclaimerContinue() {
    voucheringStateMachineDispatch({ type: eventNames.USER_REDIRECTED });
  }

  function getPaymentRedirectUrl() {
    const redirectURL = getVoucherPaymentRedirectURL(
      voucheringStateMachine.context.initiatePaymentResponse?.voucherOrderId ||
        ""
    );

    return redirectURL;
  }

  return (
    <>
      {(voucheringStateMachine.matches("initiatingPayment") ||
        voucheringStateMachine.matches("fetchingPaymentReceipt") ||
        voucheringStateMachine.matches("waitingForPaymentOutcome") ||
        voucheringStateMachine.matches("processingPaymentOutcome") ||
        voucheringStateMachine.matches("fetchingVoucherTypes")) && (
        <FullscreenLoadingIndicator
          loadingText={t("common.pleaseWait")}
          mainIconType="face"
        />
      )}

      {voucheringStateMachine.matches("buyOrUseVoucher") && (
        <AddOrUseVoucherPage
          voucher={voucheringStateMachine.context?.vouchers}
          onDoctorNurseConsultationVoucherClicked={
            onDoctorNurseConsultationVoucherClick
          }
          onPathologyTestVoucherClicked={onPathologyTestVoucherClick}
        />
      )}

      {voucheringStateMachine.matches("doctorNurseConsultSelection") && (
        <DoctorNurseVoucherTypeSelection
          onDoctorConsultClick={onDoctorVoucherClick}
          onNurseConsultClick={onNurseVoucherClick}
          selectedVoucher={voucheringStateMachine.context?.selectedVoucherType}
          onBackButtonClick={onBackButtonClick}
        />
      )}

      {voucheringStateMachine.matches("showingPrompt") && (
        <BuyVoucherPrompt
          voucherVendorDefinitionId={
            voucheringStateMachine.context?.selectedVoucherSubType
              ?.voucherVendorDefinitionId
          }
          onContinueButtonClick={onVoucherPaymentContinueButtonClick}
          onBackButtonClick={onBackButtonClick}
        />
      )}

      {voucheringStateMachine.matches("breakoutScreen") && (
        <Breakout
          type="Ecentric"
          onConfirm={onBreakoutDisclaimerContinue}
          redirectUrl={getPaymentRedirectUrl()}
          onDecline={onBackButtonClick}
        />
      )}

      {voucheringStateMachine.matches("paymentFailed") && (
        <PaymentFailure onContinue={onGoHomeButtonClick} />
      )}

      {voucheringStateMachine.matches("showingOrderSummary") &&
        voucheringStateMachine.context?.paymentReceiptResponse && (
          <VoucherOrderSummary
            voucherPaymentReceipt={
              voucheringStateMachine.context.paymentReceiptResponse
            }
            onViewReceiptClick={onViewPaymentReceiptClick}
          />
        )}

      {voucheringStateMachine.matches("showingPaymentReceipt") &&
        voucheringStateMachine.context?.paymentReceiptResponse && (
          <PaymentReceipt
            amount={
              voucheringStateMachine.context.paymentReceiptResponse
                .paymentAmount
            }
            currency={
              voucheringStateMachine.context.paymentReceiptResponse.currencyCode
            }
            transactionDate={
              voucheringStateMachine.context.paymentReceiptResponse
                .paymentUtcDateTime
            }
            referenceNumber={
              voucheringStateMachine.context.paymentReceiptResponse
                .transactionReferenceNumber
            }
            merchantId={
              voucheringStateMachine.context.paymentReceiptResponse
                .unuMerchantId
            }
            cardNumber={
              voucheringStateMachine.context.paymentReceiptResponse.cardNumber
            }
            onBackButtonClick={onBackButtonClick}
            isDiscountApplied={
              voucheringStateMachine.context.paymentReceiptResponse
                .isDiscountApplied
            }
            originalAmount={
              voucheringStateMachine.context.paymentReceiptResponse
                .originalAmount
            }
          />
        )}

      {voucheringStateMachine.matches("error") && (
        <Stack height="100vh" justifyContent="end">
          <DefaultError onComeBackLater={onGoHomeButtonClick}>
            <Typography variant="h2">{t("GenericError.title")}</Typography>
            <Typography pb={2}>{t("GenericError.content")}</Typography>
          </DefaultError>
        </Stack>
      )}

      {voucheringStateMachine.matches("exit") && <Navigate to="/" />}
    </>
  );
}
